import { AfterViewInit, ChangeDetectorRef, Component, Injector, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { factory } from './shared/app-logging';
import { AuthService } from './services/auth.service';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { RedirectService } from './services/redirect.service';
import { environment } from '../environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { GlobalNavService, MenuData } from 'ngx-global-nav';
import { UserService } from './services/user.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { CookieService as SharedCookieService } from 'ngx-shared-services';
import { TemplatePriorityComponent } from './components/template-priority/template-priority.component';
import { TemplatesPriorityService } from './services/template-priority.service';
import { flatMap as _flatmap } from 'lodash';
import { ExportService } from './services/export.service';
import { TEMPLATE_BULK_EDIT, TEMPLATE_DEFINITION, TEMPLATE_PRIORITY } from './components/common/common';
import { Product } from './model/product';

const logger = factory.getLogger('AppComponent');

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {

  private subscriptions$: Subscription[] = [];

  public isLoadingActive: boolean = false;
  // Might want to rename this property later in order not to monopolize the
  // naming. Other tabs might also want to have that property if the error
  // NG0100 returns.
  public isExportButtonDisabled: boolean = true;
  public isTemplatePriorityEditEnabled: boolean = false;
  public templatePriorityExportIsEnabled: boolean = false;
  public templatePriorityExportLoadingActive: boolean = false;
  public templatePrioritySaveIsEnabled: boolean = false;

  featureFlag = "";
  title = "XCore-ContentLibrary-UI";
  @ViewChild("checkCredentialsModal", { static: true })
  checkCredentialsModal: ModalDirective;
  checkCredentialsModalHTMLContent: string;

  menuData$: Observable<MenuData>;

  // Org Context Not needed for ContentLibrary
  // orgContext$: Observable<OrgContext>;
  // orgList: Organization[] = [];

  public pageTitle: string;

  templateTabSelected: String[] = [];
  event_types: String[] | any = [];
  allEventTypes: String[] | any = [];
  dropdownSelected: String[];
  priority: any[] = [];
  opps: Product[] = [];
  error: any;

  tabPriorityName: string = TEMPLATE_PRIORITY;
  tabBulkEditName: string = TEMPLATE_BULK_EDIT;
  tabDefinitionName: string = TEMPLATE_DEFINITION;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private redirector: RedirectService,
    private translate: TranslateService,
    private cookieService: CookieService,
    private globalNavService: GlobalNavService /* private orgSelectorService: OrgSelectorService,*/,
    private userService: UserService,
    private sharedCookieService: SharedCookieService,
    private injector: Injector,
    private cdr: ChangeDetectorRef,
    public exportService: ExportService,
    public templatePriorityService: TemplatesPriorityService,
  ) {
    this.router = this.injector.get(Router);
    this.checkCredentialsModalHTMLContent = `
          <div style="width: 100%;text-align: center; ">
                  <span style="font-size: xx-large;">
                     <i class="fa fa-spinner fa-spin"></i>
                  </span>
          </div>
          <p class="text-primary">Wait a moment, please.</p>`;

    if (this.sharedCookieService.getI18NBrand() === "") {
      this.sharedCookieService.setCookie("default_brand", "enelx");
    }

    if (this.sharedCookieService.getI18NLocale() === "") {
      this.sharedCookieService.setCookie("locale", "en-us");
    }
    this.globalNavService.setNavUrl(environment.userNavApi);
    this.subscriptions$.push(this.userService.getUserDetails().subscribe((userDetails) => {
      this.menuData$ = this.userService.getUserMenu(userDetails);
      this.globalNavService.userData$.next(userDetails);
      this.userService.setUserDetail(userDetails);
    }));

  }

  isActiveTab = (tab: string) => {
    return tab === this.pageTitle;
  };

  private updateIsExportButtonDisabledState = () => {
    const previousValue = this.isExportButtonDisabled;

    this.isExportButtonDisabled =
      !this.templatePriorityExportIsEnabled ||
      this.isTemplatePriorityEditEnabled ||
      this.templatePriorityExportLoadingActive;

    if (previousValue !== this.isExportButtonDisabled) {
      this.cdr.detectChanges();
    }
  }

  ngOnInit(): void {
    // subscribe to route changes
    this.subscriptions$.push(this.router.events.subscribe((event) => this.updatePageTitle(event)));
    // subscribe to template priority state changes
    this.subscriptions$.push(this.templatePriorityService.getAction().subscribe((value: string) => {
      if (value === 'EDIT') {
        this.isTemplatePriorityEditEnabled = true;
      }
      if (value === 'CANCEL' || value === 'SAVE_OK') {
        this.isTemplatePriorityEditEnabled = false;
      }
      this.updateIsExportButtonDisabledState();
    }));
    // subscribe to template priority save enabled changes
    this.subscriptions$.push(this.templatePriorityService.getIsSaveEnabled().subscribe((value) => {
      this.templatePrioritySaveIsEnabled = value;
    }));
    // subscribe to template priority state changes
    this.subscriptions$.push(this.templatePriorityService.getIsLoadingActive().subscribe((value: boolean) => {
      this.isLoadingActive = value;
    }));
    // subscribe to template priority export enabled changes
    this.subscriptions$.push(this.exportService.getIsExportEnabled().subscribe((value) => {
      this.templatePriorityExportIsEnabled = value;
      this.updateIsExportButtonDisabledState();
    }));
    // subscribe to template priority export loading changes
    this.subscriptions$.push(this.exportService.getIsLoadingActive().subscribe((value) => {
      this.templatePriorityExportLoadingActive = value;
      this.updateIsExportButtonDisabledState();
    }));
    // subscribe to template priority opps changes
    this.subscriptions$.push(this.templatePriorityService.getOpps().subscribe((value) => {
      this.opps = value;
    }));
  }

  ngAfterViewInit(): void {
    this.checkCredentialsModal.show();
    this.authService.checkAuthorization().subscribe(
      (res) => {
        this.checkCredentialsModal.hide();
      },
      (err) => {
        this.checkCredentialsModalHTMLContent = `
          <p class="text-primary">
              An error occurred while checking authorization. Please try again later.
              <br/><strong>${err.name ? err.name : ""}</strong>
          </p>`;
        err.message = `While checking credentials. ${err.message || ""}`;
        this.redirector.goToErrorPage(err);
        this.checkCredentialsModal.hide();
        throw err;
      }
    );
    const { defaults } = environment;

    // TODO:  There are no plans to localize ContentLibrary (https://enernoc.slack.com/archives/CBUMG9W0N/p1582218929123200)
    const locale = defaults.locale.replace("_", "-").toLowerCase();
    const brand = defaults.brand.toUpperCase();

    // TODO: Use ngx-shared-cookie-service Once this app is upgraded to Angular8
    // const locale = this.getI18NLocale() || defaults.locale;
    // const brand = this.getI18NBrand() || defaults.brand.toUpperCase();

    this.translate.setDefaultLang(
      `${defaults.locale
        .replace("_", "-")
        .toLowerCase()}.${defaults.brand.toUpperCase()}`
    );
    this.translate.use(`${locale}.${brand}`).subscribe(() => {
      document.title = this.translate.instant("APP.TITLE");
    });
  }

  ngOnDestroy(): void {
    this.subscriptions$.forEach(sub => sub.unsubscribe());
  }


  getDataFromComponent(event: TemplatePriorityComponent) {
    if (event instanceof TemplatePriorityComponent) {

    }
  }

  updatePageTitle(event): void {
    if (event instanceof NavigationEnd) {
      let route = this.activatedRoute;
      while (route.firstChild !== null) {
        route = route.firstChild;
      }
      if (route.outlet === "primary") {
        route.data.subscribe((data) => {
          this.pageTitle = data.title;
        });
      }
    }
  }

  private getI18NLocale(): string {
    const locale = this.getCookieValue("locale");
    return locale ? locale.replace("_", "-").toLowerCase() : locale;
  }

  private getI18NBrand() {
    const brand = this.getCookieValue("default_brand");
    return brand ? brand.toUpperCase() : brand;
  }

  private getCookieValue(cookieName): string {
    if (this.cookieService.check(cookieName)) {
      return this.cookieService.get(cookieName);
    } else {
      return undefined;
    }
  }

  public resetTemplatePrioritiesSubjects() {
    // this.templatePriorityService.setIsLoadingActive(false);
    // this.templatePriorityService.setIsSaveEnabled(false);
    // this.templatePriorityService.setAction('');
    // this.templatePriorityService.setError(null);
    // this.templatePriorityService.setOpps([]);
    // this.templatePriorityService.setDropdownSelectedEntries([]);
    // this.templatePriorityService.setDefaultTemplatePriorities([]);
    // this.templatePriorityService.setTemplatePriorities([]);
    // this.templatePriorityService.setEditedTemplatePriorities([]);
  }
}
